import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Button, Card, CardContent } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useSnackbar } from 'notistack';

import { COLORS } from '../../../shared/Theme';
import { formatMoney } from '../../../locales/formatModule';
import { member as v_member, basic, Values, SNACKBAR_TIME } from '../../../request/values';
import ToastStr from '../../../request/toastStr';
import { DialogConfirm, DialogCompanyApproval } from '../../../components/Dialog';

const SERVER = basic.server;
// 새로운 하단 UI를 위한 테마 설정
const THEME = {
	BACKGROUND: '#f4f7fa',
	SURFACE: '#ffffff',
	TEXT_MAIN: '#333333',
	TEXT_SUB: '#777777',
	BORDER: '#e0e5eb',
	WATER_BLUE: COLORS.WATER_BLUE,
	PUMPKIN: COLORS.PUMPKIN,
};

const useStyles = makeStyles((theme) => ({
	// ==========================================
	// [위쪽 기존 원본 UI CSS 그대로 유지]
	// ==========================================
	card: {
		background: `url("/images/info_box.png")`,
		backgroundRepeat: 'no-repeat',
		backgroundPosition: 'inherit',
		backgroundSize: 'contain',
		width: '100%',
		height: '63vw',
		maxHeight: '450px',
		border: 'none',
		boxShadow: 'none',
		maxWidth: '700px',
		margin: '0 auto', // 중앙 정렬 추가
		'& #companyName': {
			marginTop: '3%',
			color: `${COLORS.WATER_BLUE}`,
			[theme.breakpoints.up('md')]: {
				marginTop: '10%',
			},
		},
		'& #stock': {
			color: `${COLORS.WATER_BLUE}`,
		},
		'& #unit': {
			...theme.typography.h4,
			color: `${COLORS.WATER_BLUE}`,
			fontFamily: 'NanumB',
			fontWeight: 'bold',
		},
	},
	cardContent: {
		margin: '0',
		padding: '11% 7% 0',
	},
	loanButton: {
		width: '30%',
		float: 'right',
		marginTop: '7%',
		borderRadius: '8px',
		backgroundColor: `${COLORS.WHITE}`,
		[theme.breakpoints.up('lg')]: {
			marginTop: '13%',
		},
	},
	loadButtonText: {
		color: `${COLORS.WATER_BLUE}`,
		fontFamily: 'NanumB',
		fontWeight: 'bold',
	},
	assetButton: {
		width: '30%',
		float: 'right',
		borderRadius: '8px',
		backgroundColor: `${COLORS.WHITE}`,
	},
	buttonBox: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	cashButton: {
		...theme.typography.h3,
		color: `${COLORS.WATER_BLUE}`,
		fontFamily: 'NanumB',
		fontWeight: 'bold',
	},
	bigTitle: {
		fontFamily: 'NanumB',
		fontWeight: 'bold',
		fontSize: '8vw',
		'@media (min-width:748px)': {
			fontSize: '42px',
		},
	},

	// ==========================================
	// [아래쪽 팀원 현황 신규 콤팩트 UI CSS]
	// ==========================================
	teamBoard: {
		backgroundColor: THEME.SURFACE,
		borderRadius: 12,
		boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
		border: `1px solid ${THEME.BORDER}`,
		maxWidth: '700px',
		margin: '20px auto',
		padding: theme.spacing(2),
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(1.5),
	},
	boardHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingBottom: theme.spacing(1),
		borderBottom: `2px solid ${THEME.BACKGROUND}`,
	},
	boardTitle: {
		...theme.typography.h2,
		fontWeight: 'bold',
		color: THEME.TEXT_MAIN,
	},
	refreshButton: {
		borderRadius: 6,
		border: `1px solid ${THEME.BORDER}`,
		color: THEME.TEXT_SUB,
		fontSize: '0.8rem',
		fontWeight: 'bold',
		padding: '2px 10px',
		minWidth: 0,
		backgroundColor: THEME.BACKGROUND,
	},
	roleRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: theme.spacing(1.5),
		borderBottom: `1px dashed ${THEME.BORDER}`,
		paddingBottom: theme.spacing(1.5),
		marginTop: theme.spacing(0.5),
		'&:last-child': {
			borderBottom: 'none',
			paddingBottom: 0,
		},
	},
	roleLabel: {
		flex: '0 0 auto',
		maxWidth: '38%',
		paddingTop: '6px',
		fontSize: '0.85rem',
		fontWeight: 'bold',
		color: THEME.TEXT_MAIN,
		lineHeight: 1.3,
		wordBreak: 'keep-all',
	},
	membersContainer: {
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'flex-end',
		alignItems: 'center',
		alignContent: 'flex-start',
		gap: '8px',
		flex: '1 1 0',
		minWidth: 0,
	},
	memberChip: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
		maxWidth: '100%',
		backgroundColor: '#e6f3fb',
		padding: '4px 12px',
		borderRadius: 20,
		border: `1px solid #b7dff3`,
		transition: 'all 0.2s',
		fontSize: '0.85rem',
		fontWeight: 'bold',
		color: THEME.WATER_BLUE,
	},
	memberChipClickable: {
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: '#d4ecf8',
			borderColor: THEME.WATER_BLUE,
			color: '#0f7bbd',
		},
	},
	emptyText: {
		fontSize: '0.85rem',
		color: THEME.TEXT_SUB,
		paddingTop: '6px',
	},
	applicantList: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'stretch',
		gap: theme.spacing(1),
		paddingTop: theme.spacing(0.5),
		// teamBoard gap + 대표 row marginTop 보정용 하단 여백
		paddingBottom: theme.spacing(2),
		marginBottom: theme.spacing(0.5),
		borderBottom: `2px solid ${THEME.BACKGROUND}`,
	},
	applicantCard: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: theme.spacing(1),
		padding: '10px 12px',
		borderRadius: 10,
		border: `1px solid #b7dff3`,
		backgroundColor: '#e6f3fb',
	},
	applicantInfo: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1.5),
		minWidth: 0,
		flex: 1,
	},
	applicantName: {
		fontSize: '0.9rem',
		fontWeight: 'bold',
		color: THEME.TEXT_MAIN,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		lineHeight: 1.2,
	},
	applicantRole: {
		fontSize: '0.8rem',
		fontWeight: 'bold',
		color: THEME.WATER_BLUE,
		whiteSpace: 'nowrap',
		flexShrink: 0,
		lineHeight: 1.2,
	},
	applicantActions: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.75),
		flexShrink: 0,
	},
	applicantAction: {
		minWidth: 0,
		padding: '4px 12px',
		fontSize: '0.75rem',
		fontWeight: 'bold',
		borderRadius: 6,
		lineHeight: 1.2,
	},
	acceptBtn: {
		backgroundColor: THEME.WATER_BLUE,
		color: '#fff',
		'&:hover': { backgroundColor: '#0f7bbd' },
	},
	rejectBtn: {
		backgroundColor: '#fff',
		color: COLORS.RED,
		border: `1px solid ${COLORS.RED}`,
		'&:hover': { backgroundColor: '#fff0f0' },
	},
}));

const CompanyInfo: React.FC<any> = (props) => {
	const classes = useStyles();
	const { t } = useTranslation('student');
	const history = useHistory();
	const { enqueueSnackbar } = useSnackbar();
	const { member } = props;

	// 위쪽 UI 상태 (기존 유지)
	const [stock, setStock] = useState<number>(0);
	const [companyName, setCompanyName] = useState('');

	// 아래쪽 팀원 상태 (안정성 강화)
	const [teamMembers, setTeamMembers] = useState<Record<string, any[]>>({});
	const [applicants, setApplicants] = useState<any[]>([]);
	const [isCEO, setIsCEO] = useState<boolean>(false);

	// 다이얼로그 상태
	const [dialogOpen, setDialogOpen] = useState(false);
	const [selectedMember, setSelectedMember] = useState<any>(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [confirmApplicant, setConfirmApplicant] = useState<any>(null);

	// 회사 타입별 직무 배열 가져오기 (DB에 저장되는 텍스트 기준)
	const getRolesByType = () => {
		if (member.companyType === 'D') return ['CEO', '구매담당자', '판매담당자', '분해담당자'];
		if (member.companyType === 'S') return ['CEO', '구매 및 수익창출담당자', '분해담당자', '판매담당자'];
		return ['CEO', '구매담당자', '판매담당자', '생산담당자'];
	};
	const roles = getRolesByType();

	// 데이터 조회 (새로고침 버튼 공용)
	const fetchData = async () => {
		try {
			let url = new URL(basic.server + v_member.member + v_member.company_info);
			url.search = new URLSearchParams({ companyNum: member.companyNum }).toString();

			const res = await axios.get(url.toString(), { withCredentials: true });
			const data = res.data;

			if (data.company.employees.length === 0 && sessionStorage.getItem('company_reload') !== 'true') {
				sessionStorage.setItem('company_reload', 'true');
				window.location.reload();
				return;
			}

			setCompanyName(data.company.companyName);
			setStock(data.company.companyStock);

			// 팀원 분류
			const groupedMembers: Record<string, any[]> = {};
			roles.forEach((r) => (groupedMembers[r] = []));

			let userIsLeader = false;

			data.company.employees.forEach((emp: any) => {
				if (emp.grade === 'CEO' && emp.memberEmail === member.id) {
					userIsLeader = true;
				}

				if (emp.memberName && groupedMembers[emp.grade]) {
					groupedMembers[emp.grade].push(emp);
				} else if (emp.memberName && emp.grade === 'CEO') {
					if (!groupedMembers['CEO']) groupedMembers['CEO'] = [];
					groupedMembers['CEO'].push(emp);
				}
			});

			setTeamMembers(groupedMembers);
			setIsCEO(userIsLeader);

			if (userIsLeader) fetchApplicants();
		} catch (error) {
			console.error('Failed to fetch company info', error);
		}
	};

	// 가입 신청자 조회
	const fetchApplicants = async () => {
		try {
			const appUrl = new URL(basic.server + v_member.member + v_member.company_app_list);
			appUrl.search = new URLSearchParams({
				companyNum: String(member.companyNum),
				classCode: String(member.classCode ?? ''),
			}).toString();

			const res = await axios.get(appUrl.toString(), { withCredentials: true });
			const data = res.data;

			if (data?.code === Values.SUCCESS_CODE || Array.isArray(data?.appList)) {
				setApplicants(data.appList ?? []);
			} else {
				setApplicants([]);
			}
		} catch (error) {
			console.warn('신청자 목록 조회 실패', error);
			setApplicants([]);
		}
	};

	// 인앱 가입 수락/거절 핸들러
	const handleAccept = async (applicant: any) => {
		try {
			const res = await axios.post(
				SERVER + v_member.member + v_member.company_approval,
				{
					email: applicant.member_email,
					companyNum: member.companyNum,
				},
				{ withCredentials: true },
			);
			const data = res.data;

			if (data.code === Values.SUCCESS_CODE) {
				enqueueSnackbar(ToastStr.COMPANY_APPROVAL_SUCCESS_STR, { variant: 'success', autoHideDuration: SNACKBAR_TIME });
				fetchData();
			} else {
				enqueueSnackbar(ToastStr.COMPANY_APPROVAL_FAIL_STR, { variant: 'warning', autoHideDuration: SNACKBAR_TIME });
			}
		} catch (error) {
			enqueueSnackbar('수락 처리 중 오류가 발생했습니다.', { variant: 'error' });
		}
	};

	const handleReject = async (applicant: any) => {
		try {
			const res = await axios.post(
				SERVER + v_member.member + v_member.company_decline,
				{
					email: applicant.member_email,
					companyNum: member.companyNum,
				},
				{ withCredentials: true },
			);
			const data = res.data;

			if (data.code === Values.SUCCESS_CODE) {
				enqueueSnackbar(ToastStr.COMPANY_DECLINE_SUCCESS_STR, { variant: 'success', autoHideDuration: SNACKBAR_TIME });
				fetchData();
			} else {
				enqueueSnackbar(ToastStr.COMPANY_DECLINE_FAIL_STR, { variant: 'warning', autoHideDuration: SNACKBAR_TIME });
			}
		} catch (error) {
			enqueueSnackbar('거절 처리 중 오류가 발생했습니다.', { variant: 'error' });
		}
	};

	const openRejectConfirmDialog = (applicant: any) => {
		setConfirmApplicant(applicant);
		setConfirmOpen(true);
	};

	const closeConfirmDialog = () => setConfirmOpen(false);

	const confirmRejectAction = async () => {
		if (!confirmApplicant) return;
		const applicant = confirmApplicant;
		setConfirmOpen(false);
		await handleReject(applicant);
	};

	// 다이얼로그 핸들러
	const openRoleDialog = (emp: any, currentRole: string) => {
		if (!isCEO || currentRole === 'CEO') return; // 리더가 아니거나 CEO 본인이면 무시
		setSelectedMember({ ...emp, currentRole });
		setDialogOpen(true);
	};

	const closeRoleDialog = () => setDialogOpen(false);

	const submitRoleChange = async (grade: string) => {
		if (!selectedMember || grade === selectedMember.currentRole) {
			setDialogOpen(false);
			return;
		}

		try {
			const res = await axios.post(
				SERVER + v_member.member + v_member.company_update,
				{
					email: selectedMember.memberEmail,
					companyNum: member.companyNum,
					grade: grade,
				},
				{ withCredentials: true },
			);
			const data = res.data;

			if (data.code === Values.SUCCESS_CODE) {
				enqueueSnackbar(ToastStr.COMPANY_MEMBER_UPDATE_SUCCESS_STR, {
					variant: 'success',
					autoHideDuration: SNACKBAR_TIME,
				});
				setDialogOpen(false);
				fetchData();
			} else {
				enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, { variant: 'error', autoHideDuration: SNACKBAR_TIME });
			}
		} catch (error) {
			enqueueSnackbar('역할 변경 중 오류가 발생했습니다.', { variant: 'error' });
		}
	};

	useEffect(() => {
		fetchData();
	}, []);

	const goLoan = () => history.push('/LoanList');
	const goAsset = () => history.push('/CompanyAsset');

	return (
		<div className="contentCompanyInfo" data-testid="company-info-dashboard">
			{/* ==========================================
                [위쪽 기존 원본 UI 파트 그대로 유지]
               ========================================== */}
			<Card className={classes.card}>
				<CardContent className={classes.cardContent}>
					<div className={classes.buttonBox}>
						<Typography className={classes.cashButton}>{t('companyInfo.cash')}</Typography>
						<Button className={classes.assetButton} onClick={goAsset} data-testid="company-info-asset-button">
							<Typography variant="h3" className={classes.loadButtonText}>
								{t('companyInfo.assetButton')}
							</Typography>
						</Button>
					</div>
					<Typography variant="subtitle1" align="center" id="companyName" data-testid="company-info-name">
						{companyName}
					</Typography>
					<p style={{ textAlign: 'center', margin: 0 }}>
						<span id="stock" className={classes.bigTitle} data-testid="company-info-stock">
							{formatMoney(stock)}
						</span>
					</p>
					{isCEO && (
						<Button className={classes.loanButton} onClick={goLoan} data-testid="company-loan-btn">
							<Typography variant="h3" className={classes.loadButtonText}>
								{t('companyInfo.bank')}
							</Typography>
						</Button>
					)}
				</CardContent>
			</Card>

			{/* ==========================================
                [아래쪽 콤팩트 디자인 팀원 현황 보드 파트]
               ========================================== */}
			<div className={classes.teamBoard}>
				<div className={classes.boardHeader}>
					<Typography className={classes.boardTitle}>{t('companyInfo.teamStatus')}</Typography>
					<Button className={classes.refreshButton} onClick={fetchData} data-testid="company-info-refresh-btn">
						새로고침
					</Button>
				</div>

				{/* 가입 신청자: 팀원 현황과 대표 사이 */}
				{isCEO && applicants.length > 0 && (
					<div className={classes.applicantList}>
						{applicants.map((app) => (
							<div key={app.member_email} className={classes.applicantCard} data-testid={`company-info-applicant-${app.member_name}`}>
								<div className={classes.applicantInfo}>
									<span className={classes.applicantName}>{app.member_name}</span>
									<span className={classes.applicantRole}>{app.grade === 'CEO' ? t('companyInfo.ceo') : app.grade}</span>
								</div>
								<div className={classes.applicantActions}>
									<Button className={`${classes.applicantAction} ${classes.rejectBtn}`} onClick={() => openRejectConfirmDialog(app)}>
										거절
									</Button>
									<Button
										className={`${classes.applicantAction} ${classes.acceptBtn}`}
										onClick={() => handleAccept(app)}
										data-testid={`company-info-applicant-approve-btn-${app.member_name}`}
									>
										수락
									</Button>
								</div>
							</div>
						))}
					</div>
				)}

				{/* 역할별 팀원 목록 */}
				{roles.map((role) => {
					const members = teamMembers[role] ?? [];

					return (
						<div key={role} className={classes.roleRow}>
							<Typography className={classes.roleLabel}>{role === 'CEO' ? t('companyInfo.ceo') : role}</Typography>
							<div className={classes.membersContainer}>
								{members.length > 0 ? (
									members.map((emp, idx) => (
										<div
											key={emp.memberEmail ?? idx}
											className={`${classes.memberChip} ${isCEO && role !== 'CEO' ? classes.memberChipClickable : ''}`}
											onClick={() => openRoleDialog(emp, role)}
											title={isCEO && role !== 'CEO' ? '클릭하여 역할 변경' : ''}
											data-testid={`company-info-member-${emp.memberName}`}
										>
											{emp.memberName}
										</div>
									))
								) : (
									<Typography className={classes.emptyText}>-</Typography>
								)}
							</div>
						</div>
					);
				})}
			</div>

			<DialogCompanyApproval
				open={dialogOpen}
				handleClose={closeRoleDialog}
				companyName="역할 변경"
				memberName={selectedMember?.memberName}
				companyType={member.companyType}
				initialGrade={selectedMember?.currentRole}
				noConfirm={closeRoleDialog}
				yesConfirm={submitRoleChange}
			/>

			<DialogConfirm
				open={confirmOpen}
				handleClose={closeConfirmDialog}
				title="가입 거절"
				context={`${confirmApplicant?.member_name ?? ''}님의 가입을 거절하시겠습니까?`}
				yesConfirm={confirmRejectAction}
				noConfirm={closeConfirmDialog}
			/>
		</div>
	);
};

export default CompanyInfo;
